import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { toast } from "react-toastify";
import { Plus, Edit, Trash2, Save, X, Eye, EyeOff, Image, Upload } from "lucide-react";
import ImageUpload from "../../ImageUpload";
import { API, getImageUrl } from "./adminConfig";

const HomepageCarouselTab = () => {
  const [carouselItems, setCarouselItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [carouselHeadline, setCarouselHeadline] = useState('Take a tour of all the fantastic site features');
  const [savingHeadline, setSavingHeadline] = useState(false);
  const [imageLibrary, setImageLibrary] = useState([]);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [imageLibraryFor, setImageLibraryFor] = useState(''); // 'main' or 'thumbnail'

  useEffect(() => {
    fetchCarouselItems();
    fetchCarouselSettings();
    fetchImageLibrary();
  }, []);

  const fetchImageLibrary = async () => {
    try {
      // Filter for images only (exclude videos) for carousel thumbnails
      const response = await axios.get(`${API}/admin/image-library`, {
        params: { file_type: "image" }
      });
      // Backend returns array directly, not wrapped in object
      setImageLibrary(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching image library:', error);
    }
  };

  const fetchCarouselSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/carousel-settings`);
      setCarouselHeadline(response.data.headline || 'Take a tour of all the fantastic site features');
    } catch (error) {
      console.error('Error fetching carousel settings:', error);
    }
  };

  const saveCarouselHeadline = async () => {
    setSavingHeadline(true);
    try {
      await axios.post(`${API}/admin/carousel-settings`, { headline: carouselHeadline });
      toast.success('Headline updated successfully!');
    } catch (error) {
      console.error('Error saving headline:', error);
      toast.error('Error saving headline');
    } finally {
      setSavingHeadline(false);
    }
  };

  const fetchCarouselItems = async () => {
    try {
      const response = await axios.get(`${API}/admin/homepage-carousel`);
      setCarouselItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching carousel items:', error);
      toast.error('Error loading carousel items');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (type === 'video' && !file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    if (type === 'image' && !file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (100MB for video, 10MB for images)
    const maxSize = type === 'video' ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large (max ${type === 'video' ? '100' : '10'}MB)`);
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/carousel/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // Create new carousel item with uploaded file
        const newItem = {
          id: `item_${Date.now()}`,
          type: response.data.type,
          url: response.data.url,
          title: '',
          description: '',
          order: carouselItems.length,
          enabled: true
        };
        
        setEditingItem(newItem);
        toast.success('File uploaded successfully! Add title and description.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading file');
    } finally {
      setUploading(false);
    }
  };

  const handleAddVideoUrl = () => {
    if (!videoUrl.trim()) {
      toast.error('Please enter a video URL');
      return;
    }

    // Create new carousel item with URL
    const newItem = {
      id: `item_${Date.now()}`,
      type: 'video',
      url: videoUrl,
      title: '',
      description: '',
      order: carouselItems.length,
      enabled: true
    };
    
    setEditingItem(newItem);
    setVideoUrl('');
    setShowUrlInput(false);
    toast.success('Video URL added! Now add title and description.');
  };

  const saveCarouselItem = async (item) => {
    try {
      const response = await axios.post(`${API}/admin/homepage-carousel`, item);
      
      if (response.data.success) {
        toast.success('Carousel item saved successfully!');
        setEditingItem(null);
        fetchCarouselItems();
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Error saving carousel item');
    }
  };

  const deleteCarouselItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this carousel item?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/homepage-carousel/${itemId}`);
      toast.success('Carousel item deleted');
      fetchCarouselItems();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Error deleting carousel item');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-burgundy"></div>
      </div>
    );
  }

  const imageItems = carouselItems.filter(item => item.type === 'image');
  const videoItems = carouselItems.filter(item => item.type === 'video');
  // Removed carousel item limits - users can add unlimited images and videos
  const canAddImage = true;
  const canAddVideo = true;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-burgundy font-serif">Homepage Carousel</h2>
          <p className="text-burgundy/70 mt-1">Manage carousel items (unlimited images and videos)</p>
        </div>
      </div>

      {/* Carousel Headline */}
      <Card className="border-burgundy/30">
        <CardHeader>
          <CardTitle className="text-burgundy">Carousel Headline</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-burgundy/70">
            This headline appears above all carousel items and stays static.
          </p>
          <div className="flex space-x-2">
            <Input
              value={carouselHeadline}
              onChange={(e) => setCarouselHeadline(e.target.value)}
              placeholder="Enter carousel headline..."
              className="flex-1 border-burgundy/30"
            />
            <Button
              onClick={saveCarouselHeadline}
              disabled={savingHeadline}
              className="bg-burgundy hover:bg-burgundy/80 text-cream"
            >
              {savingHeadline ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-gold/10 border-gold/30">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-gold rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-burgundy">💡</span>
            </div>
            <div>
              <h4 className="font-semibold text-burgundy mb-1">Carousel Management</h4>
              <p className="text-sm text-burgundy/70">
                Add unlimited images and videos to the carousel. Items auto-rotate and videos open in a modal when clicked.
              </p>
              <p className="text-sm text-burgundy/70 mt-2">
                <strong>Current:</strong> {imageItems.length} image{imageItems.length !== 1 ? 's' : ''}{videoItems.length > 0 ? `, ${videoItems.length} video${videoItems.length !== 1 ? 's' : ''}` : ''}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {canAddVideo && (
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-burgundy mb-3">Add Video Modal (Optional)</h3>
              <p className="text-xs text-burgundy/60 mb-3">Video opens in modal when user clicks thumbnail</p>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => handleFileUpload(e, 'video')}
                disabled={uploading}
                className="hidden"
                id="video-upload"
              />
              <label
                htmlFor="video-upload"
                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  uploading ? 'border-gray-300 bg-gray-50 cursor-not-allowed' : 'border-burgundy/30 hover:border-burgundy bg-burgundy/5 hover:bg-burgundy/10'
                }`}
              >
                {uploading ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-burgundy mb-2" />
                    <span className="text-sm text-burgundy font-medium">Click to upload video</span>
                    <span className="text-xs text-burgundy/60 mt-1">MP4, WebM, MOV (max 100MB)</span>
                  </>
                )}
              </label>
              
              {/* Or Add Video URL */}
              <div className="mt-4">
                {!showUrlInput ? (
                  <Button
                    onClick={() => setShowUrlInput(true)}
                    variant="outline"
                    className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                    size="sm"
                  >
                    <Link className="h-4 w-4 mr-2" />
                    Or Add Video URL
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Input
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      placeholder="Paste YouTube or Vimeo URL..."
                      className="border-burgundy/30"
                    />
                    <div className="flex space-x-2">
                      <Button
                        onClick={handleAddVideoUrl}
                        size="sm"
                        className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
                      >
                        Add URL
                      </Button>
                      <Button
                        onClick={() => {
                          setShowUrlInput(false);
                          setVideoUrl('');
                        }}
                        size="sm"
                        variant="outline"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {canAddImage && (
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-burgundy mb-3">Upload Image</h3>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'image')}
                disabled={uploading}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  uploading ? 'border-gray-300 bg-gray-50 cursor-not-allowed' : 'border-dark-teal/30 hover:border-dark-teal bg-dark-teal/5 hover:bg-dark-teal/10'
                }`}
              >
                {uploading ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dark-teal"></div>
                ) : (
                  <>
                    <Image className="h-8 w-8 text-dark-teal mb-2" />
                    <span className="text-sm text-dark-teal font-medium">Click to upload image</span>
                    <span className="text-xs text-dark-teal/60 mt-1">JPG, PNG, WebP (max 10MB)</span>
                  </>
                )}
              </label>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Editing Modal */}
      {editingItem && (
        <Card className="border-2 border-burgundy">
          <CardHeader>
            <CardTitle className="text-burgundy">
              Edit {editingItem.type === 'video' ? 'Video' : 'Image'} Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                Title *
              </label>
              <Input
                value={editingItem.title || ''}
                onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
                placeholder="Enter a title for this item..."
                className="border-burgundy/30"
              />
              <p className="text-xs text-burgundy/60 mt-1">This helps you identify the item in the admin panel</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                Description (Optional)
              </label>
              <Textarea
                value={editingItem.description || ''}
                onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
                placeholder="Add a description..."
                className="border-burgundy/30"
                rows={3}
              />
              <p className="text-xs text-burgundy/60 mt-1">Internal note about this carousel item</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                {editingItem.type === 'video' ? 'Video' : 'Image'} URL
              </label>
              <div className="flex gap-2">
                <Input
                  value={editingItem.url}
                  onChange={(e) => setEditingItem({...editingItem, url: e.target.value})}
                  placeholder="URL..."
                  className="border-burgundy/30 bg-gray-50 flex-1"
                />
                {editingItem.type === 'image' && (
                  <Button
                    onClick={() => {
                      setImageLibraryFor('main');
                      setShowImageLibrary(true);
                    }}
                    className="bg-dark-teal hover:bg-dark-teal/80 text-white"
                  >
                    Choose from Library
                  </Button>
                )}
              </div>
              <p className="text-xs text-burgundy/60 mt-1">You can edit the URL here if needed</p>
            </div>
            
            {editingItem.type === 'video' && (
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Cover Image (Thumbnail) *
                </label>
                <div className="flex gap-2">
                  <Input
                    value={editingItem.thumbnail_url || ''}
                    onChange={(e) => setEditingItem({...editingItem, thumbnail_url: e.target.value})}
                    placeholder="https://example.com/cover-image.jpg"
                    className="border-burgundy/30 flex-1"
                  />
                  <Button
                    onClick={() => {
                      setImageLibraryFor('thumbnail');
                      setShowImageLibrary(true);
                    }}
                    className="bg-dark-teal hover:bg-dark-teal/80 text-white"
                  >
                    Choose from Library
                  </Button>
                </div>
                <p className="text-xs text-burgundy/60 mt-1">Choose a cover image that will display before the video plays</p>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                Link URL (Optional)
              </label>
              <Input
                value={editingItem.link_url || ''}
                onChange={(e) => setEditingItem({...editingItem, link_url: e.target.value})}
                placeholder="https://example.com or /tools/period-tracker"
                className="border-burgundy/30"
              />
              <p className="text-xs text-burgundy/60 mt-1">
                {editingItem.type === 'video' 
                  ? 'Optional: URL to navigate to when video is clicked (videos open in modal by default)'
                  : 'When users click this image, they\'ll be taken to this URL'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Display Order</label>
              <Input
                type="number"
                min="0"
                value={editingItem.order}
                onChange={(e) => setEditingItem({...editingItem, order: parseInt(e.target.value) || 0})}
                placeholder="0"
                className="border-burgundy/30"
              />
              <p className="text-xs text-burgundy/60 mt-1">Lower numbers display first (0, 1, 2, etc.)</p>
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={() => saveCarouselItem(editingItem)}
                className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream"
              >
                Save Item
              </Button>
              <Button
                onClick={() => setEditingItem(null)}
                variant="outline"
                className="border-burgundy/30"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Carousel Items */}
      {carouselItems.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-burgundy">Current Carousel Items</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {carouselItems.map((item) => (
              <Card key={item.id}>
                <CardContent className="p-4">
                  <div className="aspect-video bg-gray-100 rounded-lg mb-3 overflow-hidden">
                    {item.type === 'video' ? (
                      <video src={item.url} className="w-full h-full object-cover" />
                    ) : (
                      <img src={item.url} alt={item.title} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-burgundy uppercase">
                        {item.type}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${item.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {item.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <h4 className="font-semibold text-burgundy text-sm">{item.title || 'Untitled'}</h4>
                    <p className="text-xs text-burgundy/70 line-clamp-2">{item.description || 'No description'}</p>
                    <div className="flex space-x-2 pt-2">
                      <Button
                        onClick={() => setEditingItem(item)}
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => deleteCarouselItem(item.id)}
                        size="sm"
                        variant="destructive"
                        className="text-xs"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Image Library Modal */}
      {showImageLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[80vh] overflow-auto bg-white border-burgundy/20">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="font-serif text-burgundy">
                  Choose Image from Library
                </CardTitle>
                <Button
                  variant="ghost"
                  onClick={() => setShowImageLibrary(false)}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {imageLibrary.map((image) => (
                  <div
                    key={image.id}
                    onClick={() => {
                      if (imageLibraryFor === 'main') {
                        setEditingItem({...editingItem, url: image.url});
                      } else if (imageLibraryFor === 'thumbnail') {
                        setEditingItem({...editingItem, thumbnail_url: image.url});
                      }
                      setShowImageLibrary(false);
                    }}
                    className="cursor-pointer border-2 border-burgundy/20 rounded-lg overflow-hidden hover:border-dark-teal transition-colors"
                  >
                    <img
                      src={image.url}
                      alt={image.alt_text || 'Library image'}
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-2 bg-burgundy/5">
                      <p className="text-xs text-burgundy truncate">{image.alt_text || 'Untitled'}</p>
                    </div>
                  </div>
                ))}
              </div>
              {imageLibrary.length === 0 && (
                <div className="text-center py-8 text-burgundy/60">
                  No images in library. Upload images in the Image Library tab.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};



// Content Boxes Tab Component

export default HomepageCarouselTab;
